import { type ReactNode, useMemo } from "react";
import { Screen } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { BuiHeader, type IBuiHeaderProps } from "@btnet/blueprint-components";
import { BTOverflow } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import {
    BdsIconClockCounterClockwise,
    BdsIconExport,
    BdsIconFunnel,
    BdsIconGiveFeedback,
    BdsIconHelp,
    BdsIconPrinter,
    BdsIconSettings,
    BdsLink,
} from "@buildertrend/components";

import { useTracking } from "utilities/analytics/analytics";

import { BTFilterButton } from "commonComponents/btWrappers/BTFilterButton/BTFilterButton";
import {
    BT_LIST_HEADER_OVERFLOW_PADDING,
    type IBTListHeaderActionButtonsProps,
    type IBTListHeaderIcon,
    type IBTListHeaderIconDefaults,
    ICON_VARIANT_ORDER,
} from "commonComponents/btWrappers/BTListHeader/BTListHeader.types";

import "./BTListHeader.less";

import { BTButton, type IBTButtonPropsBase } from "../BTButton/BTButton";
import { BTMenu } from "../BTMenu/BTMenu";
import { BTPopover } from "../BTPopover/BTPopover";

/** Default values for each actionIcon. */
const mappedActionIconDefaults: Record<IBTListHeaderIcon["icon"], IBTListHeaderIconDefaults> = {
    feedback: {
        icon: <BdsIconGiveFeedback />,
        testid: "feedback",
        text: "Feedback",
        ariaLabel: "Feedback",
        tooltip: "Give Feedback",
    },
    help: {
        icon: <BdsIconHelp />,
        testid: "help",
        text: "Help",
        ariaLabel: "Help",
        tooltip: "Go to Help Center",
    },
    settings: {
        icon: <BdsIconSettings />,
        testid: "settings",
        text: "Settings",
        ariaLabel: "Settings",
    },
    activity: {
        icon: <BdsIconClockCounterClockwise />,
        testid: "activity",
        text: "Activity",
        ariaLabel: "Activity",
    },
    history: {
        icon: <BdsIconClockCounterClockwise />,
        testid: "history",
        text: "History",
        ariaLabel: "History",
    },
    print: {
        icon: <BdsIconPrinter />,
        testid: "print",
        text: "Print",
        ariaLabel: "Print",
        tooltip: "Print all",
    },
    export: {
        icon: <BdsIconExport />,
        testid: "export",
        text: "Export",
        ariaLabel: "Export",
        tooltip: "Export all",
    },
    filter: {
        icon: <BdsIconFunnel />,
        testid: "filter",
        text: "Filter",
        ariaLabel: "Filter",
    },
};

/** mappedActionIconDefaults defines the order of the presentation. Icons present in mappedActionIconDefaults are positioned to the right of icons that are not present. */
const sortActions = (a: IBTListHeaderIcon, b: IBTListHeaderIcon) => {
    const aIndex = ICON_VARIANT_ORDER.indexOf(a.icon);
    const bIndex = ICON_VARIANT_ORDER.indexOf(b.icon);

    // If both icons are in the order list, sort by their position
    if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
    }

    // If only one is in the order list, prioritize it
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    // If neither is in the order list, maintain original order
    return 0;
};

function getActionButtonProps(
    action: IBTListHeaderIcon,
    iconDefaults: IBTListHeaderIconDefaults,
    testid: string,
): IBTButtonPropsBase {
    return {
        "aria-label": iconDefaults.ariaLabel,
        type: "tertiary",
        "data-testid": testid
            ? `${testid}-${iconDefaults.testid}-content-header-button`
            : `${iconDefaults.testid}-content-header-button`,
        selected: action.selected,
        loading: action.loading,
    };
}

function getMenuActions(menuActions: IBTListHeaderIcon[] | undefined, testid: string) {
    if (!menuActions || menuActions.length === 0) {
        return null;
    }

    return [...menuActions].sort(sortActions).map((action) => {
        const iconDefaults = mappedActionIconDefaults[action.icon];
        const btnProps = getActionButtonProps(action, iconDefaults, testid);
        const iconButton = action.to ? (
            <BdsLink
                testid={`${btnProps["data-testid"]}-link`}
                to={action.to}
                ariaLabel={iconDefaults.ariaLabel}
            >
                <BTButton {...btnProps}>{iconDefaults.text}</BTButton>
            </BdsLink>
        ) : (
            <BTButton {...btnProps} onClick={action.onClick}>
                {iconDefaults.text}
            </BTButton>
        );

        return (
            <BTMenu.Item
                id={action.icon}
                key={action.icon}
                testid={
                    testid
                        ? `${testid}-${iconDefaults.testid}-content-header-button-menu-item`
                        : `${iconDefaults.testid}-content-header-button-menu-item`
                }
            >
                {iconButton}
            </BTMenu.Item>
        );
    });
}

function getInlineActions(
    inlineActions: IBTListHeaderIcon[] | undefined,
    testid: string,
    filterCount?: number,
) {
    if (!inlineActions || inlineActions.length === 0) {
        return null;
    }

    return [...inlineActions].sort(sortActions).map((action) => {
        const iconDefaults = mappedActionIconDefaults[action.icon];
        const btnProps = getActionButtonProps(action, iconDefaults, testid);

        if (action.icon === "filter") {
            return (
                <BTOverflow.Item
                    id={action.icon}
                    key={action.icon}
                    pinned
                    overflowContent={
                        <BTFilterButton
                            {...btnProps}
                            onClick={action.onClick}
                            icon={iconDefaults.icon}
                            filterCount={filterCount}
                        />
                    }
                >
                    <BTPopover content={iconDefaults.tooltip ?? iconDefaults.ariaLabel}>
                        <BTFilterButton
                            {...btnProps}
                            onClick={action.onClick}
                            icon={iconDefaults.icon}
                            filterCount={filterCount}
                        />
                    </BTPopover>
                </BTOverflow.Item>
            );
        }

        const iconButton = action.to ? (
            <span>
                <BdsLink
                    testid={`${btnProps["data-testid"]}-link`}
                    to={action.to}
                    ariaLabel={iconDefaults.ariaLabel}
                >
                    <BTButton {...btnProps} icon={iconDefaults.icon} />
                </BdsLink>
            </span>
        ) : (
            <BTButton {...btnProps} onClick={action.onClick} icon={iconDefaults.icon} />
        );

        const overflowContent = action.to ? (
            <BdsLink
                testid={`${btnProps["data-testid"]}-link`}
                to={action.to}
                ariaLabel={iconDefaults.ariaLabel}
            >
                <BTButton {...btnProps}>{iconDefaults.text}</BTButton>
            </BdsLink>
        ) : (
            <BTButton {...btnProps} onClick={action.onClick}>
                {iconDefaults.text}
            </BTButton>
        );

        return (
            <BTOverflow.Item
                id={action.icon}
                key={action.icon}
                pinned={action.pinned}
                overflowContent={overflowContent}
            >
                <BTPopover
                    content={
                        action.popoverContent ?? iconDefaults.tooltip ?? iconDefaults.ariaLabel
                    }
                    widthType={action.popoverWidthType}
                    trigger={action.popoverContent ? "click" : "hover"}
                    placement="top"
                >
                    {iconButton}
                </BTPopover>
            </BTOverflow.Item>
        );
    });
}

const BTListHeaderActionButtons = ({
    iconActions,
    primaryAction,
    secondaryActions,
    testid,
    filterCount,
    jobName,
    entityType,
}: IBTListHeaderActionButtonsProps) => {
    const jobNameContent = entityType ? (
        <span>
            {jobName} &bull; {entityType}
        </span>
    ) : (
        <span>{jobName}</span>
    );

    const fullHeading = (
        <h1 className="bt-list-header-heading truncate text-gray-90">{jobNameContent}</h1>
    );

    const isMdBreakpoint = useMediaQuery(`(max-width: ${Screen.md}px)`);

    // These are the actions that will be visible either as inline buttons or in the overflow menu. Hidden actions will not be rendered at all.
    const visibleActions = useMemo(
        () => iconActions?.filter((action) => !action.isHidden) ?? [],
        [iconActions],
    );

    // Separate the visible actions into those that should be rendered as inline buttons and those that should only be rendered in the overflow menu based on the `inMenu` property or `inMenuMdBreakpoint` property.
    const menuActions = useMemo(() => {
        const mdBreakpointActions = visibleActions.filter(
            (action) => isMdBreakpoint && action.inMenuMdBreakpoint,
        );

        const inMenuActions = visibleActions.filter((action) => action.inMenu);
        return [...mdBreakpointActions, ...inMenuActions];
    }, [visibleActions, isMdBreakpoint]);

    // Actions that are not hidden and not marked as inMenu will be rendered as inline buttons.
    const inlineActions = useMemo(
        () =>
            visibleActions.filter(
                (action) => !action.inMenu && !(isMdBreakpoint && action.inMenuMdBreakpoint),
            ),
        [visibleActions, isMdBreakpoint],
    );

    const secondaryActionIds = useMemo(
        () => secondaryActions?.map((_, index) => `secondary-action-${index}-overflow-item`) ?? [],
        [secondaryActions],
    );

    const overflowIds = useMemo(() => {
        const inlineIds = inlineActions.map((action) => action.icon);
        return [...inlineIds, ...secondaryActionIds];
    }, [inlineActions, secondaryActionIds]);

    // Calculate the number of visible actions to determine the necessary padding for the overflow menu.
    const visibleActionCount =
        visibleActions.length + (secondaryActions?.length ?? 0) + (primaryAction ? 1 : 0);

    return (
        <div className="flex flex-row items-center w-full">
            {(jobNameContent || entityType) && fullHeading}
            <div className="bt-list-header-actions-flex flex-1">
                <BTOverflow
                    className="bt-list-header-overflow justify-end"
                    testid={testid}
                    overflowDirection="start"
                    // Assuming each item has 16px of padding (8px on each side)
                    padding={BT_LIST_HEADER_OVERFLOW_PADDING * visibleActionCount}
                >
                    <BTOverflow.Menu testid={`${testid}-menu`} ids={overflowIds}>
                        {getMenuActions(menuActions, testid)}
                    </BTOverflow.Menu>
                    {getInlineActions(inlineActions, testid, filterCount)}
                    {secondaryActions?.map((action, index) => (
                        <BTOverflow.Item
                            id={secondaryActionIds[index]}
                            key={secondaryActionIds[index]}
                        >
                            {action}
                        </BTOverflow.Item>
                    ))}
                    {primaryAction && (
                        <BTOverflow.Item id="primary-action-overflow-item" pinned>
                            {primaryAction}
                        </BTOverflow.Item>
                    )}
                </BTOverflow>
            </div>
        </div>
    );
};

const BTListHeaderInternal = (props: IBuiHeaderProps) => {
    const { className, style, testid, ariaLabel, children } = props;
    const { Track } = useTracking({ component: "BTListHeader", uniqueId: testid });

    return (
        <Track>
            <BuiHeader
                className={cn("bt-list-header bg-gray-0 pt-4 pb-3", className)}
                style={style}
                testid={testid}
                ariaLabel={ariaLabel}
            >
                {children}
            </BuiHeader>
        </Track>
    );
};

interface IBTListHeaderTitleProps {
    children?: ReactNode;
}

const BTListHeaderTitle = (props: IBTListHeaderTitleProps) => {
    return <h2 className="bt-list-header-title text-gray-90">{props.children}</h2>;
};

export const BTListHeader = Object.assign(BTListHeaderInternal, {
    Title: BTListHeaderTitle,
    ActionButtons: BTListHeaderActionButtons,
});
